import {Collection} from '@tinct/web-collection';
import {useAudiencesListColumns} from './hooks/use-audiences-list-columns';
import {useGetAudiences} from '@tinct/core-api';
import {LayoutContent, LayoutHeader, LayoutTopbar, useBreadcrumb} from '@tinct/web-layout';
import {useGetCurrentWorkspaceId, useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {Button} from '@tinct/web-components';
import {PlusIcon, BookmarkIcon} from 'lucide-react';
import {useState} from 'react';
import {CreateAudienceModal} from '../../modals/create-audience-modal/create-audience-modal';
import {DeleteAudienceModal} from '../../modals/delete-audience-modal/delete-audience-modal';
import {ReadAudience} from '@tinct/core-api-models';
import {useStaticAssetsUrl} from "@tinct/core-utils";

function AudiencesEmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="flex items-center justify-center size-10 rounded-lg bg-muted mb-4">
        <BookmarkIcon className="size-6" />
      </div>
      <p className="text-lg font-medium text-foreground">No audience yet</p>
      <p className="text-sm text-muted-foreground mb-6">
        Add your first audience
      </p>
      <Button onClick={onCreate}>
        <PlusIcon className="size-4" />
        Create an audience
      </Button>
    </div>
  );
}

export function ListAudiencesPage() {
  const workspaceId = useGetCurrentWorkspaceId();
  const workspaceBaseUrl = useCurrentWorkspaceUrlPrefix();
  const assetsUrl = useStaticAssetsUrl();
  const breadcrumbs = useBreadcrumb([
    { label: 'Dashboard', to: `${workspaceBaseUrl}/dashboard` },
  ], true);
  const [isCreateAudienceModalOpen, setIsCreateAudienceModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAudience, setSelectedAudience] = useState<ReadAudience | null>(null);

  const columns = useAudiencesListColumns({
    onDeleteAudience: (audience) => {
      setSelectedAudience(audience);
      setIsDeleteModalOpen(true);
    },
  });

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar
          breadcrumbs={breadcrumbs}
        />
      </header>
      <LayoutContent>
        <div className="flex w-full justify-between items-end">
          <LayoutHeader
            pageTitle="Audiences"
            pageDescription="Manage your target company lists."
          />
          <img
            src={`${assetsUrl}/audience-header-illustration.png`}
            alt=""
            className="h-[120px] w-[360px] shrink-0 object-cover pointer-events-none"
          />
        </div>

        <CreateAudienceModal
          open={isCreateAudienceModalOpen}
          onOpenChange={setIsCreateAudienceModalOpen}
          completedUrl={`${workspaceBaseUrl}/audiences`}
        />
        {selectedAudience && (
          <DeleteAudienceModal
            audience={selectedAudience}
            open={isDeleteModalOpen}
            onOpenChange={setIsDeleteModalOpen}
          />
        )}
        <Collection
          toolbarActions={
            <Button key="create-audience" onClick={() => setIsCreateAudienceModalOpen(true)}>
                <PlusIcon />
                Create audience
            </Button>
          }
          useLocationAsState
          defaultSorting={[{ id: 'createdAt', desc: true }]}
          columns={columns}
          filtersConfiguration={{
            textFilter: {
              placeholder: 'Search audiences...',
              queryParamName: 'term',
            },
          }}
          useQueryFn={useGetAudiences}
          staticParams={{
            organizationId: workspaceId,
          }}
          queryFields={{
            id: null,
            name: null,
            lastImportJob: null,
            campaigns: {
              fields: {
                id: null,
                name: null,
              },
            },
            statistics: {
              fields: {
                leadsCount: null,
                campaignsCount: null,
              },
            },
            createdAt: null,
          }}
          refetchInterval={5000}
          emptyState={<AudiencesEmptyState onCreate={() => setIsCreateAudienceModalOpen(true)} />}
        />
      </LayoutContent>
    </div>
  );
}
